import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import {
  subscribeToFamily,
  fetchFamilyMembers,
  fetchInviteCode,
  createFamily as svcCreateFamily,
  joinFamily as svcJoinFamily,
  leaveFamily as svcLeaveFamily,
  rotateInviteCode as svcRotateCode,
  kickMember as svcKickMember,
  transferOwnership as svcTransferOwnership,
} from '../services/familyService';
import { clearOwnFamilyId } from '../services/authService';

export const FamilyContext = createContext(null);

/**
 * Provider del hogar activo. Se monta DENTRO del AuthProvider.
 *
 * Estado:
 *  - family: doc del hogar suscrito en tiempo real
 *  - members: perfiles de los miembros (carga lazy, refetch al cambiar memberIds)
 *  - inviteCode: código activo (carga lazy)
 *  - status: idle | loading | ready | error
 *
 * No suscribimos a members en tiempo real para minimizar lecturas.
 * Refetcheamos cuando memberIds cambia (detectado por hash).
 */
export const FamilyProvider = ({ children }) => {
  const { profile, refreshProfile } = useAuth();
  const familyId = profile?.familyId ?? null;

  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [inviteCode, setInviteCode] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  // Hash de memberIds para detectar cambios sin re-renders inútiles
  const memberIdsHash = useMemo(
    () => family?.memberIds?.slice().sort().join(',') ?? '',
    [family?.memberIds]
  );

  /**
   * Suscripción al hogar.
   * Se re-suscribe si cambia el familyId del perfil.
   */
  useEffect(() => {
    if (!familyId) {
      setFamily(null);
      setMembers([]);
      setInviteCode(null);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    let cancelled = false;
    const unsubscribe = subscribeToFamily(
      familyId,
      (data) => {
        setFamily(data);
        setStatus('ready');
        setError(null);

        // Si el hogar fue eliminado (doc no existe), refrescar el perfil:
        // la Cloud Function de limpieza eventual va a poner familyId=null,
        // pero mientras tanto el cliente queda con state stale. Refresh
        // nos da el estado actualizado del profile (eventualmente null).
        if (!data) {
          refreshProfile();
        }
      },
      (err) => {
        if (cancelled) return;

        // "Fui kickeado": el doc del family aún existe pero la rule de
        // read me empezó a rechazar (porque `auth.uid` ya no está en
        // memberIds). Firestore SDK emite un error con code='permission-denied'.
        //
        // Distinguimos esto de otros errores transitorios (red, etc.) por
        // el code del error original encadenado (FirestoreError lo guarda
        // como `cause`).
        const causeCode = err?.cause?.code;
        if (causeCode === 'permission-denied' && profile?.uid && familyId) {
          // Limpiar el propio profile (self-update legal) + refresh para
          // que ProtectedRoute con requireFamily redirija a /onboarding.
          clearOwnFamilyId(profile.uid)
            .then(() => refreshProfile())
            .catch((cleanupErr) => {
              console.error('[FamilyContext] Cleanup post-kick falló:', cleanupErr);
            });
          // Marcamos el estado como "expulsado" para que la UI pueda
          // mostrar un mensaje si lo necesita antes del redirect.
          setError(
            Object.assign(new Error('Ya no perteneces a este hogar'), {
              code: 'KICKED',
            })
          );
          setStatus('error');
          return;
        }

        setError(err);
        setStatus('error');
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [familyId, profile?.uid, refreshProfile]);

  /**
   * Cargar miembros cuando cambia memberIds.
   * No bloqueamos el render del hogar mientras se cargan.
   */
  useEffect(() => {
  if (!memberIdsHash) {
    setMembers([]);
    return;
  }

  let cancelled = false;
  // Reconstruir los IDs desde el hash para no depender de family.memberIds.
  const memberIds = memberIdsHash.split(',');
  fetchFamilyMembers(memberIds)
    .then((data) => {
      if (!cancelled) setMembers(data);
    })
    .catch((err) => {
      console.error('[FamilyContext] Failed to load members:', err);
      if (!cancelled) setMembers([]);
    });

  return () => {
    cancelled = true;
  };
}, [memberIdsHash]);

  /**
   * Cargar código de invitación. Solo el owner lo necesita visible
   * (los miembros no necesitan invitar nuevos), pero por simplicidad
   * lo cargamos para todos. Las reglas de Firestore filtran si fuera necesario.
   */
  useEffect(() => {
    if (!familyId) {
      setInviteCode(null);
      return;
    }

    let cancelled = false;
    fetchInviteCode(familyId)
      .then((code) => {
        if (!cancelled) setInviteCode(code);
      })
      .catch((err) => {
        console.error('[FamilyContext] Failed to load invite code:', err);
        if (!cancelled) setInviteCode(null);
      });

    return () => {
      cancelled = true;
    };
  }, [familyId]);

  // ===== Mutations =====
  // Estas funciones envuelven los servicios y refrescan el perfil cuando
  // el familyId del usuario cambia (creación / unión / salida).

  const createFamily = useCallback(
    async (name) => {
      if (!profile?.uid) throw new Error('Usuario no autenticado');
      const result = await svcCreateFamily({ uid: profile.uid, name });
      await refreshProfile();
      return result;
    },
    [profile?.uid, refreshProfile]
  );

  const joinFamily = useCallback(
    async (code) => {
      if (!profile?.uid) throw new Error('Usuario no autenticado');
      const result = await svcJoinFamily({ uid: profile.uid, code });
      await refreshProfile();
      return result;
    },
    [profile?.uid, refreshProfile]
  );

  const leaveFamily = useCallback(async () => {
    if (!profile?.uid || !familyId) throw new Error('Sin hogar activo');
    await svcLeaveFamily({ uid: profile.uid, familyId });
    await refreshProfile();
  }, [profile?.uid, familyId, refreshProfile]);

  const rotateInviteCode = useCallback(async () => {
    if (!profile?.uid || !familyId) throw new Error('Sin hogar activo');
    const newCode = await svcRotateCode({ uid: profile.uid, familyId });
    setInviteCode(newCode);
    return newCode;
  }, [profile?.uid, familyId]);

  const kickMember = useCallback(
    async (memberUid) => {
      if (!profile?.uid || !familyId) throw new Error('Sin hogar activo');
      return svcKickMember({ uid: profile.uid, familyId, memberUid });
    },
    [profile?.uid, familyId]
  );

  const transferOwnership = useCallback(
    async (newOwnerUid) => {
      if (!profile?.uid || !familyId) throw new Error('Sin hogar activo');
      return svcTransferOwnership({ uid: profile.uid, familyId, newOwnerUid });
    },
    [profile?.uid, familyId]
  );

  const value = useMemo(
    () => ({
      family,
      members,
      inviteCode,
      status,
      error,
      isOwner: family?.ownerId === profile?.uid,
      isLoading: status === 'loading',
      createFamily,
      joinFamily,
      leaveFamily,
      rotateInviteCode,
      kickMember,
      transferOwnership,
    }),
    [
      family,
      members,
      inviteCode,
      status,
      error,
      profile?.uid,
      createFamily,
      joinFamily,
      leaveFamily,
      rotateInviteCode,
      kickMember,
      transferOwnership,
    ]
  );

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
};

export const useFamily = () => {
  const ctx = useContext(FamilyContext);
  if (!ctx) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return ctx;
};